import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    updateRequest,
    updateOptions,
} from '@/actions/wizard/RequestWizardActions';
import Input from '@/elements/Input';

const TIER_KEYS = { 1: 'relaxed', 2: 'elevated', 3: 'luxe' };

export default function BudgetOptions({ budgetTypes, selectedOption }) {
    const dispatch = useDispatch();
    const { meta, request, options } = useSelector(
        (state) => state.requestWizard
    );

    const dynamicTier = (value) =>
        (budgetTypes?.dynamicPricing &&
            options.dynamicPricing?.tiers?.[TIER_KEYS[value]]) ||
        null;

    // Only while the FIRST model price is in flight. On a refetch (guest count
    // changed) the previous price stays put rather than flickering to a
    // placeholder on every keystroke.
    const awaitingFirstPrice =
        budgetTypes?.dynamicPricing &&
        options.dynamicPricingLoading &&
        !options.dynamicPricing;

    // Update suggested budget
    useEffect(() => {
        // Same reason as the card placeholder: a hardcoded total here would be
        // contradicted by the model total a moment later.
        if (!selectedOption || !selectedOption.priceRange || awaitingFirstPrice) {
            dispatch(updateOptions('suggestedBudget', false));
            return;
        }

        // Multiply by number of days
        const days = Math.max(1, (request.mealDate?.length || 1));

        const dynamic = dynamicTier(selectedOption.value);
        if (dynamic) {
            dispatch(updateOptions('suggestedBudget', [dynamic.total * days, false]));
            return;
        }

        let min = selectedOption.priceRange[0] * request.guests * days,
            max = (selectedOption.priceRange[1] ?? 3000) * request.guests * days;

        // Apply (per-day) lower bound scaled by days when applicable
        const perDayLowerBound = selectedOption.lowerBound || selectedOption.minSpend;
        const lowerBound = perDayLowerBound ? perDayLowerBound * days : 0;
        if (min < lowerBound) min = lowerBound;
        if (max <= min) max = false;

        dispatch(updateOptions('suggestedBudget', [min, max]));
    }, [
        meta.step,
        selectedOption,
        request.guests,
        request.mealDate,
        meta.features,
        options.dynamicPricing,
        awaitingFirstPrice,
    ]);

    const budgetOptions = budgetTypes?.options;
    if (!budgetOptions) return;

    const minSpendCopy =
        request.type == 'Full-time chef' ||
        request.type == 'Multi-day chef hire'
            ? 'Min day rate'
            : 'Min spend';

    const mappedOptions = budgetOptions.map((option, idx) => {
        const tier = dynamicTier(option.value);

        return {
            label: (
                <>
                    <strong>{option.label}</strong>
                    {(option.priceRange || option.minSpend) && (
                        <p className="m-0 fs-14 fw-normal">
                            {awaitingFirstPrice ? (
                                <span
                                    aria-label="Loading price"
                                    className="inline-block h-[10px] w-[56px] animate-pulse rounded bg-slate-200 align-middle"
                                />
                            ) : tier ? (
                                `~${meta.currencySign}${tier.pp}pp`
                            ) : (
                                <>
                                    {option.priceRange?.length === 2 &&
                                        `${meta.currencySign}${option.priceRange?.[0]} - ${meta.currencySign}${option.priceRange?.[1]}pp`}
                                    {option.priceRange?.length === 1 &&
                                        `${meta.currencySign}${option.priceRange?.[0]}pp+`}
                                </>
                            )}
                            {(tier || option.priceRange) &&
                                option.minSpend &&
                                ' | '}
                            {option.minSpend &&
                                `${minSpendCopy} ${meta.currencySign}${option.minSpend.toLocaleString()}`}
                        </p>
                    )}
                    {option.description && (
                        <p className="m-0 fs-14 fw-normal">
                            {option.description}
                        </p>
                    )}
                    {idx == 1 && (
                        <span className="mt-1 badge badge-violet-200">
                            Most popular
                        </span>
                    )}
                </>
            ),
            value: option.value,
        };
    });

    return (
        <Input
            cols={
                meta.isMobile
                    ? 1
                    : budgetOptions.length < 3
                    ? budgetOptions.length
                    : 3
            }
            inputType="checkbox"
            value={[request.budgetType]}
            handleChange={(v) => dispatch(updateRequest('budgetType', v))}
            className={`input__checkbox -radio [&_.form-styling:nth-child(2)_label]:border-2`}
            optionClass="[&>span]:w-full"
            options={mappedOptions}
        />
    );
}
